import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
  Switch,
} from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { Field, Form, Formik } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  ButtonSize,
  PrimaryCTAButton,
  SecondaryCTAButton,
} from "../../../components/CTAButtonComponents";
import {
  useAdminUserDetails,
  useUpdateCompanyBillingDetails,
} from "../../../queries/companyStats/esopStats";

type Props = {
  existingData?: any;
  isShow: boolean;
  companyId: string;
  onClose: () => void;
  onSuccess?: () => void;
};

const savedFormData: Record<string, any> = {};
const convertToISODate = (dateStr: string): string => {
  if (!dateStr) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }

  try {
    const date = new Date(dateStr);
    if (!date.getTime()) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error("Date conversion error:", error);
    return "";
  }
};
export const BillingDataPopup = ({
  existingData,
  isShow,
  companyId,
  onClose,
  onSuccess,
}: Props) => {
  const { mutate: updateBilling, isLoading: isUpdating } =
    useUpdateCompanyBillingDetails();

  const [currentFormValues, setCurrentFormValues] = useState<any>(null);

  useEffect(() => {
    if (isShow && companyId) {
      if (savedFormData[companyId]) {
        setCurrentFormValues(savedFormData[companyId]);
      } else if (existingData) {
        const rawOnboardedDate = existingData.companyOnboardedDate || "";
        const converted = convertToISODate(rawOnboardedDate);

        const formValues = {
          companyOnboardedDate: converted || rawOnboardedDate,
          companyGoLiveDate: existingData.companyGoLiveDate || "",
          isHissaCustomer: Boolean(existingData.isHissaCustomer),
          _originalOnboardedDate: rawOnboardedDate,
        };

        savedFormData[companyId] = formValues;
        setCurrentFormValues(formValues);
      } else {
        // no data at all
        setCurrentFormValues({
          companyOnboardedDate: "",
          companyGoLiveDate: "",
          isHissaCustomer: false,
          _originalOnboardedDate: "",
        });
      }
    }
  }, [isShow, companyId, existingData]);
  const handleSubmit = (values: any) => {
    const payload = {
      data: {
        esopCompanyId: companyId,
        billingData: {
          companyOnboardedDate: values.companyOnboardedDate?.trim()
            ? values.companyOnboardedDate
            : values._originalOnboardedDate || "",

          companyGoLiveDate: values.companyGoLiveDate,
          isHissaCustomer: values.isHissaCustomer,
        },
      },
    };

    updateBilling(payload, {
      onSuccess: () => {
        savedFormData[companyId] = values;
        toast.success("Billing data updated successfully");
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      },
      onError: (err: any) => {
        toast.error("Failed to update billing data");
        console.error(err);
      },
    });
  };

  if (!isShow) return null;

  if (!currentFormValues) {
    return (
      <Transition show={isShow} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          open={isShow}
          onClose={onClose}
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75" />
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <DialogPanel className="w-full max-w-lg rounded-lg bg-white p-8 text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading billing details...</p>
              </DialogPanel>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  }

  return (
    <Transition show={isShow} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <TransitionChild as={Fragment}>
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <TransitionChild as={Fragment}>
              <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white px-6 py-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <DialogTitle className="text-xl font-medium mb-6">
                  Update Billing Data
                </DialogTitle>

                <Formik
                  initialValues={currentFormValues}
                  enableReinitialize={true}
                  validationSchema={Yup.object({
                    companyGoLiveDate: Yup.string().required(
                      "Live Date is required",
                    ),
                  })}
                  onSubmit={handleSubmit}
                >
                  {({ values, setFieldValue, errors, touched }) => {
                    // Auto-save form state as user types
                    useEffect(() => {
                      savedFormData[companyId] = values;
                    }, [values]);

                    return (
                      <Form className="space-y-6">
                        {/* Onboarded Date - Disabled */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Onboarded Date
                          </label>
                          <Field
                            type="date"
                            name="companyOnboardedDate"
                            readOnly
                            className="mt-1 block w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-500 cursor-not-allowed"
                          />
                        </div>

                        {/* Live Date */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Live Date <span className="text-red-500">*</span>
                          </label>
                          <Field
                            type="date"
                            name="companyGoLiveDate"
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-primary focus:border-primary"
                          />
                          {errors.companyGoLiveDate &&
                            touched.companyGoLiveDate &&
                            typeof errors.companyGoLiveDate === "string" && (
                              <p className="mt-1 text-xs text-red-600">
                                {errors.companyGoLiveDate}
                              </p>
                            )}
                        </div>

                        {/* Is Hissa Customer */}
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-gray-700">
                            Is Hissa Customer
                          </label>
                          <Switch
                            checked={values.isHissaCustomer}
                            onChange={(v) =>
                              setFieldValue("isHissaCustomer", v)
                            }
                            className={`${
                              values.isHissaCustomer
                                ? "bg-primary"
                                : "bg-gray-300"
                            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                          >
                            <span
                              className={`${
                                values.isHissaCustomer
                                  ? "translate-x-6"
                                  : "translate-x-1"
                              } inline-block h-4 w-4 rounded-full bg-white transition-transform`}
                            />
                          </Switch>
                        </div>

                        <div className="mt-8 flex justify-end gap-3">
                          <SecondaryCTAButton
                            buttonSize={ButtonSize.MEDIUM}
                            onClick={onClose}
                            disabled={isUpdating}
                          >
                            Cancel
                          </SecondaryCTAButton>
                          <PrimaryCTAButton
                            buttonSize={ButtonSize.MEDIUM}
                            type="submit"
                            disabled={isUpdating}
                          >
                            {isUpdating ? "Saving..." : "Save Changes"}
                          </PrimaryCTAButton>
                        </div>
                      </Form>
                    );
                  }}
                </Formik>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
